/**
 * CPP (Construction Phase Plan) wizard — per-step Zod schemas.
 *
 * Lifted verbatim from `apps/mobile/screens/wizards/WizardCppScreen.tsx`
 * (Phase 14). 8 content steps + a preview step.
 *
 * Site rules + welfare arrangements + the emergency facts (emergency
 * procedures, assembly point, nearest hospital) are single project-level facts
 * edited once in project settings (the former `rulesWelfare` and `emergency`
 * steps were removed) — they render into the CPP from the project row.
 *
 * Step order:
 *
 *   1. context       — job description, address, dates
 *   2. description   — project description
 *   3. hazards       — multi-select with severity
 *   4. controls      — per-hazard control entries
 *   5. organisation  — principal contractor + works supervisor + first aid
 *   6. hsPolicy      — health & safety policy statement
 *   7. comms         — communication procedures
 *   8. monitoring    — monitoring + review approach
 *   9. preview       — read-only summary (no validation)
 *
 * The "lightweight" CPP variant lives in `./lightweight-cpp.ts` — it
 * persists as `DocumentType.CPP` in Prisma but has a wholly different
 * step list and field set (see CLAUDE.md memory note
 * `project_lightweight_cpp`).
 */
import { z } from "zod";

// ─── Shared sub-schemas ────────────────────────────────────────────────────

export const CppHazardSeveritySchema = z.enum(["low", "medium", "high"]);
export type CppHazardSeverity = z.infer<typeof CppHazardSeveritySchema>;

export const CppHazardEntrySchema = z.object({
  id: z.string(),
  label: z.string().min(1),
  severity: CppHazardSeveritySchema,
});
export type CppHazardEntry = z.infer<typeof CppHazardEntrySchema>;

export const CppControlMeasureSchema = z.object({
  hazardId: z.string(),
  description: z.string().min(5, "Describe the control measure"),
});
export type CppControlMeasure = z.infer<typeof CppControlMeasureSchema>;

// ─── Per-step schemas ──────────────────────────────────────────────────────

export const CppContextStepSchema = z.object({
  jobDescription: z
    .string()
    .min(10, "Describe the job in at least 10 characters"),
  siteAddress: z.string().min(3, "Site address is required"),
  startDate: z.string().optional().default(""),
  endDate: z.string().optional().default(""),
});
export type CppContextStepData = z.infer<typeof CppContextStepSchema>;

export const CppDescriptionStepSchema = z.object({
  projectDescription: z
    .string()
    .min(20, "Describe the project in at least 20 characters"),
});
export type CppDescriptionStepData = z.infer<typeof CppDescriptionStepSchema>;

// Site rules + welfare arrangements are single project-level facts now — edited
// once in project settings and rendered into the CPP from the project row — so
// the CPP wizard no longer has a rulesWelfare step.

export const CppHazardsStepSchema = z.object({
  hazards: z.array(CppHazardEntrySchema).min(1, "Pick at least one hazard"),
});
export type CppHazardsStepData = z.infer<typeof CppHazardsStepSchema>;

export const CppControlsStepSchema = z.object({
  controls: z
    .array(CppControlMeasureSchema)
    .min(1, "Add at least one control measure"),
});
export type CppControlsStepData = z.infer<typeof CppControlsStepSchema>;

export const CppOrganisationStepSchema = z.object({
  principalContractor: z
    .string()
    .min(2, "Name the principal contractor (or your firm)"),
  worksSupervisorName: z.string().min(2, "Name the works supervisor"),
  firstAidArrangements: z
    .string()
    .min(5, "Describe first aid arrangements"),
});
export type CppOrganisationStepData = z.infer<
  typeof CppOrganisationStepSchema
>;

// Emergency procedures + assembly point + nearest hospital are single
// project-level facts now — edited once in project settings and rendered into
// the CPP from the project row — so the CPP wizard no longer has an emergency
// step. (nearestHospital is geocoded once on project create.)

export const CppHsPolicyStepSchema = z.object({
  healthAndSafetyPolicy: z
    .string()
    .min(20, "Provide a health & safety policy statement"),
});
export type CppHsPolicyStepData = z.infer<typeof CppHsPolicyStepSchema>;

export const CppCommsStepSchema = z.object({
  communicationProcedures: z
    .string()
    .min(10, "Describe communication procedures"),
});
export type CppCommsStepData = z.infer<typeof CppCommsStepSchema>;

export const CppMonitoringStepSchema = z.object({
  monitoringAndReview: z
    .string()
    .min(10, "Describe monitoring + review approach"),
});
export type CppMonitoringStepData = z.infer<typeof CppMonitoringStepSchema>;

/** Preview is read-only — no validation. */
export const CppPreviewStepSchema = z.object({});
export type CppPreviewStepData = z.infer<typeof CppPreviewStepSchema>;

// ─── Step ordering + composite ─────────────────────────────────────────────

export const CPP_WIZARD_STEPS = [
  { id: "context", schema: CppContextStepSchema },
  { id: "description", schema: CppDescriptionStepSchema },
  { id: "hazards", schema: CppHazardsStepSchema },
  { id: "controls", schema: CppControlsStepSchema },
  { id: "organisation", schema: CppOrganisationStepSchema },
  { id: "hsPolicy", schema: CppHsPolicyStepSchema },
  { id: "comms", schema: CppCommsStepSchema },
  { id: "monitoring", schema: CppMonitoringStepSchema },
  { id: "preview", schema: CppPreviewStepSchema },
] as const;

export const CppWizardFullSchema = z.object({
  ...CppContextStepSchema.shape,
  ...CppDescriptionStepSchema.shape,
  ...CppHazardsStepSchema.shape,
  ...CppControlsStepSchema.shape,
  ...CppOrganisationStepSchema.shape,
  ...CppHsPolicyStepSchema.shape,
  ...CppCommsStepSchema.shape,
  ...CppMonitoringStepSchema.shape,
});
export type CppWizardData = z.infer<typeof CppWizardFullSchema>;
